import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * MemoryVisualizer — What ICE does, as a short animation
 * Turns from many sessions sit in four lanes. When a new question arrives, the turns that matter are picked
 * from each lane and placed, in order, in the context window; the rest stay where they are.
 * Plays when scrolled into view and again every 2.5 minutes. Reduced motion shows the finished state and never loops.
 *
 * Importers: src/pages/ICEPage.jsx
 * Data schema: Props accept storedColor (string), recalledColor (string), beatId (string), showLabels (boolean, lane names),
 * className. The scene is fixed and illustrative, not live data.
 * Claims illustrated (see content/website-copy-deck.md): turn-granular storage, multi-lane retrieval,
 * cross-session persistence, the useful window.
 */

const LANES = ['Episodic', 'Semantic', 'Procedural', 'Working'];
const SESSIONS = [1, 7, 13, 19, 25, 31, 37, 47]; // the session each column of frames comes from
const RELEVANT = [1, 3, 5, 7]; // frame index picked in each lane, top to bottom: sessions 7, 19, 31, 47

const FRAME = { x0: 100, step: 40, w: 32, h: 36 };
const LANE_Y = [70, 122, 174, 226]; // vertical centre of each lane
const WINDOW = { x: 500, y: 44, w: 260, h: 252 };
const SLOT = (k) => ({ x: 516, y: 88 + k * 50, w: 228, h: 40 });

const ORDER = ['idle', 'query', 'scan', 'send', 'done'];
const NEXT = { query: ['scan', 900], scan: ['send', 1600], send: ['done', 1800], done: ['query', 150000] }; // after 2.5 minutes it plays again by itself

const frameCenter = (lane, idx) => ({ x: FRAME.x0 + idx * FRAME.step + FRAME.w / 2, y: LANE_Y[lane] });

const MemoryVisualizer = ({
  storedColor = '#6FB6E8',
  recalledColor = '#1E7FD1',
  beatId,
  showLabels = true,
  className = '',
}) => {
  const reducedMotion = useReducedMotion();
  const rootRef = useRef(null);
  const [phase, setPhase] = useState('idle');
  const at = (p) => ORDER.indexOf(phase) >= ORDER.indexOf(p);

  // Start once, when scrolled into view
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setPhase((p) => (p === 'idle' ? (reducedMotion ? 'done' : 'query') : p));
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  // Move through the sequence
  useEffect(() => {
    const step = NEXT[phase];
    if (!step || reducedMotion) return undefined;
    const timer = setTimeout(() => setPhase(step[0]), step[1]);
    return () => clearTimeout(timer);
  }, [phase, reducedMotion]);

  const t = (seconds, delay = 0) => (reducedMotion ? { duration: 0 } : { duration: seconds, delay, ease: [0.16, 1, 0.3, 1] });

  return (
    <div ref={rootRef} data-testid="memory-visualizer" data-phase={phase} data-beat-id={beatId} className={className}>
      <svg
        viewBox="0 0 760 400"
        role="img"
        aria-label="Diagram. Over 47 sessions, conversation turns are stored in four lanes: episodic, semantic, procedural and working. When a new question arrives, the relevant turns from each lane are placed, in order, in the context window."
        className="w-full h-auto"
      >
        {/* Titles */}
        <text x={FRAME.x0} y="22" fill="rgba(255,255,255,0.55)" fontSize="11" letterSpacing="1.5">
          STORED MEMORY · 47 SESSIONS
        </text>

        {/* Lanes and their turns */}
        {LANES.map((lane, li) => (
          <g key={lane}>
            {showLabels && (
              <text x="0" y={LANE_Y[li] + 4} fill="rgba(255,255,255,0.7)" fontSize="12">
                {lane}
              </text>
            )}
            {SESSIONS.map((session, idx) => {
              const picked = RELEVANT[li] === idx;
              const scanned = at('scan');
              return (
                <motion.rect
                  key={session}
                  x={FRAME.x0 + idx * FRAME.step}
                  y={LANE_Y[li] - FRAME.h / 2}
                  width={FRAME.w}
                  height={FRAME.h}
                  rx="6"
                  fill={picked && scanned ? storedColor : 'rgba(255,255,255,0.06)'}
                  stroke={picked && scanned ? storedColor : 'rgba(255,255,255,0.18)'}
                  strokeWidth="1.5"
                  initial={false}
                  animate={{ opacity: !scanned ? 0.9 : picked ? 1 : 0.22, fillOpacity: picked && scanned ? 0.35 : 1 }}
                  transition={t(0.5)}
                />
              );
            })}
          </g>
        ))}

        {/* Time axis */}
        <line x1={FRAME.x0} y1="262" x2={FRAME.x0 + 7 * FRAME.step + FRAME.w} y2="262" stroke="rgba(255,255,255,0.25)" />
        <text x={FRAME.x0} y="282" fill="rgba(255,255,255,0.55)" fontSize="11">
          Session 1
        </text>
        <text x={FRAME.x0 + 7 * FRAME.step + FRAME.w} y="282" fill="rgba(255,255,255,0.55)" fontSize="11" textAnchor="end">
          Session 47
        </text>

        {/* The new question */}
        <motion.g initial={false} animate={{ opacity: at('query') ? 1 : 0, y: at('query') ? 0 : 8 }} transition={t(0.5)}>
          <rect x={FRAME.x0} y="304" width="332" height="40" rx="20" fill="rgba(111,182,232,0.12)" stroke={storedColor} strokeOpacity="0.6" />
          <text x={FRAME.x0 + 166} y="329" fill="#FFFFFF" fontSize="13" textAnchor="middle">
            Session 48: a new question arrives
          </text>
        </motion.g>

        {/* Scan: a band sweeps across the lanes while the relevant turns light up */}
        <motion.rect
          y="46"
          width="30"
          height="206"
          rx="6"
          fill={storedColor}
          initial={false}
          animate={{ x: phase === 'scan' ? [FRAME.x0 - 6, FRAME.x0 + 7 * FRAME.step - 6] : FRAME.x0 - 6, opacity: phase === 'scan' ? [0, 0.16, 0] : 0 }}
          transition={reducedMotion ? { duration: 0 } : { duration: 1.5, ease: 'easeInOut' }}
        />

        {/* Context window */}
        <rect x={WINDOW.x} y={WINDOW.y} width={WINDOW.w} height={WINDOW.h} rx="16" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.18)" />
        <text x={WINDOW.x + 16} y={WINDOW.y + 26} fill="rgba(255,255,255,0.55)" fontSize="11" letterSpacing="1.5">
          CONTEXT WINDOW
        </text>

        {RELEVANT.map((idx, li) => {
          const slot = SLOT(li);
          const from = frameCenter(li, idx);
          const label = `Session ${SESSIONS[idx]} · ${LANES[li]}`;
          return (
            <g key={label}>
              {/* a faint path from the stored turn to its place in the window, and a pulse travelling along it */}
              <motion.line
                x1={from.x}
                y1={from.y}
                x2={slot.x}
                y2={slot.y + slot.h / 2}
                stroke={storedColor}
                strokeDasharray="3 5"
                initial={false}
                animate={{ opacity: at('send') ? 0.35 : 0 }}
                transition={t(0.4, li * 0.3)}
              />
              <motion.circle
                r="4"
                fill={storedColor}
                initial={false}
                animate={phase === 'send' ? { cx: [from.x, slot.x], cy: [from.y, slot.y + slot.h / 2], opacity: [0, 1, 0] } : { cx: from.x, cy: from.y, opacity: 0 }}
                transition={reducedMotion ? { duration: 0 } : { duration: 0.8, delay: li * 0.3, ease: 'easeInOut' }}
              />
              <motion.g initial={false} animate={{ opacity: at('send') ? 1 : 0.14 }} transition={t(0.45, phase === 'send' ? li * 0.3 + 0.5 : 0)}>
                <rect x={slot.x} y={slot.y} width={slot.w} height={slot.h} rx="10" fill="rgba(30,127,209,0.14)" stroke={recalledColor} strokeOpacity="0.8" />
                <text data-testid="window-item" x={slot.x + 16} y={slot.y + 25} fill="#FFFFFF" fontSize="13">
                  {label}
                </text>
              </motion.g>
            </g>
          );
        })}

        <motion.text
          x={WINDOW.x + WINDOW.w / 2}
          y={WINDOW.y + WINDOW.h + 24}
          fill="rgba(255,255,255,0.7)"
          fontSize="12"
          textAnchor="middle"
          initial={false}
          animate={{ opacity: phase === 'done' ? 1 : 0 }}
          transition={t(0.5)}
        >
          Only what matters, in order
        </motion.text>

        <text x="760" y="392" fill="rgba(255,255,255,0.4)" fontSize="11" textAnchor="end">
          Illustrative example
        </text>
      </svg>

    </div>
  );
};

export { MemoryVisualizer };
export default MemoryVisualizer;
